import type { LandingDraftPayload } from "@/services/api/eventSettings";

/**
 * JUB-151: owner preview links for the public page.
 *
 * `preview` carries a token the API mints for the authenticated owner
 * (HMAC-signed, bound to the event and its owner, short-lived). It is the only
 * thing that lifts the publish check and the password gate.
 *
 * `draft` carries the owner's unsaved landing overrides, base64 JSON built in
 * the browser. It is NOT a credential: the server ignores it, and the page
 * applies it only when the API confirmed preview mode — otherwise a crafted
 * link could make a real event page show invented text or venues.
 */
export const PREVIEW_PARAM = "preview";
export const DRAFT_PARAM = "draft";

export const encodeDraftParam = (payload: LandingDraftPayload): string | null => {
  if (typeof window === "undefined" || typeof window.btoa !== "function") {
    return null;
  }
  try {
    const json = JSON.stringify(payload);
    return window.btoa(
      encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1: string) =>
        String.fromCharCode(Number.parseInt(p1, 16)),
      ),
    );
  } catch (error) {
    console.error("[previewLink] Failed to encode draft payload", error);
    return null;
  }
};

export const decodeDraftParam = (token: string): LandingDraftPayload | null => {
  if (typeof window === "undefined" || typeof window.atob !== "function") {
    return null;
  }
  try {
    const decoded = window.atob(token);
    const json = decodeURIComponent(
      Array.from(decoded)
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join(""),
    );
    const parsed: unknown = JSON.parse(json);
    if (
      parsed &&
      typeof parsed === "object" &&
      "eventUuid" in parsed &&
      typeof parsed.eventUuid === "string" &&
      "overrides" in parsed &&
      parsed.overrides
    ) {
      return parsed as LandingDraftPayload;
    }
  } catch (error) {
    console.warn("[previewLink] Failed to decode draft payload", error);
  }
  return null;
};

/**
 * The owner's preview URL, or "" while no preview token is available yet.
 * Uses the event uuid, not the slug: the slug field may hold an unsaved value.
 */
export const buildPreviewHref = (
  origin: string,
  eventUuid: string,
  previewToken: string | null,
  draftParam: string | null,
): string => {
  if (!origin || !eventUuid || !previewToken) {
    return "";
  }
  const params = new URLSearchParams({ [PREVIEW_PARAM]: previewToken });
  if (draftParam) {
    params.set(DRAFT_PARAM, draftParam);
  }
  return `${origin.replace(/\/$/, "")}/@${eventUuid}?${params.toString()}`;
};

/** Draft overrides apply only on a server-confirmed preview of that same event. */
export const shouldApplyDraft = (
  event: { uuid: string; previewMode?: boolean } | null,
  draft: LandingDraftPayload | null,
): draft is LandingDraftPayload & { overrides: NonNullable<LandingDraftPayload["overrides"]> } =>
  Boolean(event?.previewMode && draft && draft.eventUuid === event.uuid && draft.overrides);
